import React, { FC, useRef, useEffect, MouseEventHandler } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import scrollIntoView from 'scroll-into-view';
import { Link } from 'dibs-elements/exports/Link';
import styles from './main.scss';

export type BreadcrumbsProps = {
    breadcrumbs: {
        displayName: string;
        path: string;
    }[];

    animate?: boolean;
    duration?: number;
    onClick?: MouseEventHandler;
    isLastItemActive?: boolean;
};

type BreadcrumbItemProps = {
    displayName: string;
    path: string;
    onClick?: MouseEventHandler;
};

const BreadcrumbItem: FC<BreadcrumbItemProps> = ({ displayName, path, onClick }) => (
    <li className={styles.item}>
        <Link dataTn="breadcrumb-item" href={path} onClick={onClick}>
            {displayName}
        </Link>
    </li>
);

const BreadcrumbLastItem: FC<{ displayName: string }> = ({ displayName }) => (
    <li data-tn="breadcrumb-item-last" className={styles.lastItem}>
        {displayName}
    </li>
);

const scrollToEnd = (el: HTMLElement, duration: number = 1000): void => {
    const lastItem = el.querySelector(`.${styles.lastItem}`);
    scrollIntoView(lastItem, {
        time: duration,
    });
};

const messages = defineMessages({
    label: { id: 'Breadcrumbs.label', defaultMessage: 'Breadcrumbs' },
});

export const Breadcrumbs: FC<BreadcrumbsProps> = ({
    breadcrumbs = [],
    onClick = () => {},
    isLastItemActive = false,
    animate,
    duration,
}) => {
    const intl = useIntl();
    const wrapperRef = useRef<HTMLOListElement | null>(null);

    useEffect(() => {
        if (animate && wrapperRef.current) {
            scrollToEnd(wrapperRef.current, duration);
        }
    }, [animate, duration, wrapperRef]);

    return (
        <nav aria-label={intl.formatMessage(messages.label)}>
            <ol ref={wrapperRef} className={styles.wrapper} data-tn="breadcrumbs">
                {breadcrumbs.map((breadcrumb, i) => {
                    const { displayName, path } = breadcrumb;
                    const key = `${displayName}${i}`;
                    const lastItem = breadcrumbs.length === i + 1;
                    return lastItem && !isLastItemActive ? (
                        <BreadcrumbLastItem key={key} displayName={displayName} />
                    ) : (
                        <BreadcrumbItem
                            key={key}
                            displayName={displayName}
                            path={path}
                            onClick={onClick}
                        />
                    );
                })}
            </ol>
        </nav>
    );
};
